import React, { useState, useEffect } from 'react';
import { useToast } from '../../components/ui/Toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import assignmentService from '../../services/assignmentService';
import conferenceService from '../../services/conferenceService';
import PaperList from './components/PaperList';
import ReviewerPool from './components/ReviewerPool';
import AssignmentWorkspace from './components/AssignmentWorkspace';
import AssignmentStats from './components/AssignmentStats';

const ManualReviewerAssignmentInterface = () => {
  // Replaces alert(): non-blocking, styled, translatable, and errors
  // persist until dismissed instead of vanishing.
  const { success: toastSuccess, error: toastError } = useToast();
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Accepts both spellings. The app used ?id= on six routes and
  // ?conferenceId= on three, so links between them only worked if you knew
  // which one each page wanted. Everything now *emits* ?conferenceId=;
  // reading ?id= as well keeps already-shared and bookmarked links alive.
  const conferenceId = searchParams?.get('conferenceId') || searchParams?.get('id');
  const { user } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [conference, setConference] = useState(null);
  const [papers, setPapers] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('title');
  const [showWorkloadWarning, setShowWorkloadWarning] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/user-sign-in');
    } else if (conferenceId) {
      loadData();
    }
  }, [user, conferenceId, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [confData, papersData, reviewersData, statsData] = await Promise.all([
        conferenceService?.getById(conferenceId),
        assignmentService?.getUnassignedPapers(conferenceId),
        assignmentService?.getReviewersForConference(conferenceId),
        assignmentService?.getAssignmentStats(conferenceId)
      ]);

      setConference(confData);
      setPapers(papersData);
      setReviewers(reviewersData);
      setStats(statsData);
    } catch (err) {
      console.error('Load data error:', err);
      setError(err?.message || t('reviewerAssignment.errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleAssignReviewer = async (paperId, reviewerId) => {
    try {
      const paper = papers?.find(p => p?.id === paperId);
      const reviewer = reviewers?.find(r => r?.id === reviewerId);

      if (!paper || !reviewer) return;

      // Check conflicts
      const conflicts = assignmentService?.detectConflicts(paper, reviewer);
      if (conflicts?.length > 0) {
        const confirmAssign = window.confirm(
          t('reviewerAssignment.warnings.conflictDetected', { conflicts: conflicts?.map(c => c?.message)?.join(', ') })
        );
        if (!confirmAssign) return;
      }

      // Check workload
      const avgWorkload = reviewers?.reduce((sum, r) => sum + r?.currentWorkload, 0) / reviewers?.length;
      if (reviewer?.currentWorkload > avgWorkload + 2) {
        setShowWorkloadWarning(true);
        setTimeout(() => setShowWorkloadWarning(false), 3000);
      }

      await assignmentService?.assignReviewer(conferenceId, paperId, reviewerId);
      await loadData();
      setSelectedPaper(null);
      setSelectedReviewer(null);
    } catch (err) {
      console.error('Assign reviewer error:', err);
      toastError(err?.message || t('reviewerAssignment.errors.failedToAssign'));
    }
  };

  const handleRemoveAssignment = async (paperId, reviewerId) => {
    try {
      await assignmentService?.removeAssignment(paperId, reviewerId);
      await loadData();
    } catch (err) {
      console.error('Remove assignment error:', err);
      toastError(err?.message || t('reviewerAssignment.errors.failedToRemove'));
    }
  };

  const getFilteredPapers = () => {
    let filtered = papers;

    // Search filter
    if (searchQuery?.trim()) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(p =>
        p?.title?.toLowerCase()?.includes(query) ||
        p?.keywords?.some(k => k?.toLowerCase()?.includes(query)) ||
        p?.authorName?.toLowerCase()?.includes(query)
      );
    }

    // Status filter
    if (filterStatus === 'unassigned') {
      filtered = filtered?.filter(p => p?.assignedReviewers?.length === 0);
    } else if (filterStatus === 'partial') {
      filtered = filtered?.filter(p => 
        p?.assignedReviewers?.length > 0 && !p?.isFullyAssigned
      );
    } else if (filterStatus === 'complete') {
      filtered = filtered?.filter(p => p?.isFullyAssigned);
    }

    // Sort
    if (sortBy === 'title') {
      filtered?.sort((a, b) => a?.title?.localeCompare(b?.title));
    } else if (sortBy === 'date') {
      filtered?.sort((a, b) => new Date(b?.submittedAt) - new Date(a?.submittedAt));
    } else if (sortBy === 'priority') {
      filtered?.sort((a, b) => a?.assignedReviewers?.length - b?.assignedReviewers?.length);
    }

    return filtered;
  };

  const calculateTargetWorkload = () => {
    if (!papers?.length || !reviewers?.length) return 0;
    const totalReviews = papers?.length * (conference?.minReviews || 3);
    return Math.ceil(totalReviews / reviewers?.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">{t('reviewerAssignment.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <p className="text-foreground font-semibold mb-2">{t('reviewerAssignment.errors.title')}</p>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={loadData}>{t('reviewerAssignment.retry')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <UniversalNavbar />
      <main className="page-shell py-8">
        {/* Header */}
        {/* Sticks below the app chrome rather than at viewport top, where it
            used to slide underneath the navbar. */}
        <div className="bg-card border-b border-border sticky top-[var(--chrome-offset)] z-sticky">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground">{t('reviewerAssignment.title')}</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('reviewerAssignment.subtitle', { conferenceName: conference?.name })}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate(`/conference-management-dashboard?conferenceId=${conferenceId}`)}
              >
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                {t('reviewerAssignment.backToDashboard')}
              </Button>
            </div>

            {/* Stats */}
            {stats && <AssignmentStats stats={stats} targetWorkload={calculateTargetWorkload()} />}

            {/* Workload Warning */}
            {showWorkloadWarning && (
              <div className="mt-4 p-3 bg-accent-subtle border border-accent/30 rounded-lg flex items-center">
                <Icon name="AlertTriangle" size={16} className="text-accent mr-2" />
                <span className="text-sm text-accent">
                  {t('reviewerAssignment.warnings.workloadAboveAverage')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Main Content - Three Panel Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-6 h-[calc(100vh-200px)]">
          {/* Left Panel - Papers */}
          <div className="lg:col-span-4 bg-card rounded-lg border border-border overflow-hidden flex flex-col">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground mb-3">{t('reviewerAssignment.panels.papers.title')}</h2>
              <Input
                placeholder={t('reviewerAssignment.panels.papers.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="mb-3"
                icon="Search"
              />
              <div className="flex gap-2 mb-2">
                <Select
                  value={filterStatus}
                  onChange={(value) => setFilterStatus(value)}
                  className="flex-1"
                >
                  <option value="all">{t('reviewerAssignment.panels.papers.filters.allPapers')}</option>
                  <option value="unassigned">{t('reviewerAssignment.panels.papers.filters.unassigned')}</option>
                  <option value="partial">{t('reviewerAssignment.panels.papers.filters.partiallyAssigned')}</option>
                  <option value="complete">{t('reviewerAssignment.panels.papers.filters.fullyAssigned')}</option>
                </Select>
                <Select
                  value={sortBy}
                  onChange={(value) => setSortBy(value)}
                  className="flex-1"
                >
                  <option value="title">{t('reviewerAssignment.panels.papers.sorting.byTitle')}</option>
                  <option value="date">{t('reviewerAssignment.panels.papers.sorting.byDate')}</option>
                  <option value="priority">{t('reviewerAssignment.panels.papers.sorting.byPriority')}</option>
                </Select>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              <PaperList
                papers={getFilteredPapers()}
                selectedPaper={selectedPaper}
                onSelectPaper={setSelectedPaper}
                minReviews={conference?.minReviews || 3}
                filtered={Boolean(searchQuery?.trim() || filterStatus !== 'all')}
                onClearFilters={() => { setSearchQuery(''); setFilterStatus('all'); }}
              />
            </div>
          </div>

          {/* Center Panel - Reviewers */}
          <div className="lg:col-span-4 bg-card rounded-lg border border-border overflow-hidden flex flex-col">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground mb-2">{t('reviewerAssignment.panels.reviewerPool.title')}</h2>
              <p className="text-xs text-muted-foreground">
                {t('reviewerAssignment.panels.reviewerPool.targetWorkload', { count: calculateTargetWorkload() })}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto">
              <ReviewerPool
                reviewers={reviewers}
                selectedReviewer={selectedReviewer}
                onSelectReviewer={setSelectedReviewer}
                targetWorkload={calculateTargetWorkload()}
                selectedPaper={selectedPaper}
              />
            </div>
          </div>

          {/* Right Panel - Assignment Workspace */}
          <div className="lg:col-span-4 bg-card rounded-lg border border-border overflow-hidden flex flex-col">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">{t('reviewerAssignment.panels.workspace.title')}</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              <AssignmentWorkspace
                selectedPaper={selectedPaper}
                selectedReviewer={selectedReviewer}
                papers={papers}
                reviewers={reviewers}
                onAssign={handleAssignReviewer}
                onRemove={handleRemoveAssignment}
                calculateExpertiseMatch={assignmentService?.calculateExpertiseMatch}
                detectConflicts={assignmentService?.detectConflicts}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ManualReviewerAssignmentInterface;
